import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@kalos/ui';
import { type ReactNode } from 'react';

import { useFieldLayout } from '../hooks/useMedia';
import { FieldFullScreenDialog } from './FieldFullScreenDialog';
import { bottomSheetAboveKeyboardClass, bottomSheetBodyClass } from './keyboardAwareClasses';

type DialogOrSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Opens it when tapped. Omit it to open from elsewhere through `open`. */
  trigger?: ReactNode;
  /** While true (a form is saving), it can't be closed. */
  isBusy?: boolean;
  /** Classes for the desktop dialog only. */
  dialogClassName?: string;
  /** No longer needed: every sheet now has a worded Close. Kept so callers still compile. */
  showCloseButton?: boolean;
  /**
   * What the task gets on phones and tablets. `sheet` (the default) is a quick task: pick a
   * value, change a status, one to three fields, as a bottom sheet. `fullscreen` is a longer
   * task: a form with many fields, a list to manage, a search to pick from, as the whole screen
   * rising from the bottom with a worded way out at the top. Desktop gets its dialog either way.
   */
  size?: 'sheet' | 'fullscreen';
  /** Desktop's footer buttons. */
  footer?: ReactNode;
  /**
   * The phone's buttons under the content (`fullscreen` only). Leave out Cancel or Close: the
   * top of the screen has it already. Defaults to `footer`; `null` for none.
   */
  phoneFooter?: ReactNode;
  /** The worded way out at the top of the full screen: Cancel for a form, Close for something to read, Back. */
  closeLabel?: 'Cancel' | 'Close' | 'Back';
  children: ReactNode;
};

/**
 * A dialog on desktop; on phones and tablets a bottom sheet or the whole screen, by `size`
 * (design system section 8). It uses the same `useFieldLayout()` breakpoint as the field screens
 * that open it, so a screen showing its phone layout never opens a centred dialog. The sheet has
 * a worded Close at the top right, never an icon X.
 */
export const DialogOrSheet = ({
  open,
  onOpenChange,
  title,
  description,
  trigger,
  isBusy = false,
  dialogClassName,
  size = 'sheet',
  footer,
  phoneFooter,
  closeLabel = 'Cancel',
  children,
}: DialogOrSheetProps) => {
  const isFieldLayout = useFieldLayout();
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isBusy) return;
    onOpenChange(nextOpen);
  };

  if (isFieldLayout && size === 'fullscreen') {
    const buttons = phoneFooter !== undefined ? phoneFooter : footer;
    return (
      <>
        {trigger}
        <FieldFullScreenDialog
          open={open}
          onOpenChange={handleOpenChange}
          title={title}
          description={description}
          closeLabel={closeLabel}
          isBusy={isBusy}
        >
          <div className="flex flex-col gap-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            {children}
            {buttons && <div className="flex flex-col gap-2 pt-2">{buttons}</div>}
          </div>
        </FieldFullScreenDialog>
      </>
    );
  }

  if (isFieldLayout) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
        <SheetContent
          side="bottom"
          // The side padding is on the scrolling body, not the sheet: the full-width option lists
          // (`-mx-4`) and the Close button's `-mr-3` then stay inside it instead of widening it.
          className={cn(
            bottomSheetAboveKeyboardClass,
            'rounded-t-xl px-0 pb-[calc(env(safe-area-inset-bottom)+1rem)] md:px-0',
          )}
          bodyClassName={cn(bottomSheetBodyClass, 'px-4 md:px-6')}
          closeButtonClassName="hidden"
        >
          {/* Every sheet has the same way out, in the same place: a worded Close at the top
              right. A form's own Cancel beside its Save stays as well. */}
          <SheetHeader className="pb-4 text-left">
            <div className="flex items-start justify-between gap-3">
              <SheetTitle className="min-w-0 flex-1 truncate pt-2">{title}</SheetTitle>
              <Button
                type="button"
                variant="ghost"
                className="-mr-3 -mt-1 shrink-0"
                disabled={isBusy}
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </div>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          {children}
          {footer && <div className="flex flex-col gap-2 pt-4">{footer}</div>}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className={dialogClassName}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
